import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import includes from 'lodash.includes';
import remove from 'lodash.remove';
import { RemoveScroll } from 'react-remove-scroll';

import CloseIcon from '@material-ui/icons/Close';
import Collapse from '@material-ui/core/Collapse';
import Dialog from '@material-ui/core/Dialog';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import IconButton from '@material-ui/core/IconButton';

import Image from '../../misc/Image';
import RequestRecheckView from './RequestRecheckView';
import ThumbnailPreviewAccountDetails from './ThumbnailPreviewAccountDetails';
import ThumbnailPreviewEventDetails from './ThumbnailPreviewEventDetails';

import GenerateEventCertificateMutation from '../../../mutations/GenerateEventCertificateMutation';
import RestartProductionMutation from '../../../mutations/RestartProductionMutation';

import environment from '../../../environment';
import { getPhotoVariant } from '../../../utils/common';
import { THUMBNAIL, PREVIEW, SOCIAL_MEDIA, ORIGINAL } from '../../../utils/variables';

class ThumbnailPreview extends Component {
  constructor(props) {
    super(props);

    this.hiddenLinkRef = React.createRef();

    this.state = {
      pendingRequests: [],
      certificates: [],
      showDetails: false,
      uploadRestarted: false,
      isSmallDevice: window.innerWidth <= 768,
    };

    this.handleWindowSizeChange = this.handleWindowSizeChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleShowDetailsClick = this.handleShowDetailsClick.bind(this);
    this.handleDownloadCertificate = this.handleDownloadCertificate.bind(this);
    this.handleRestartUpload = this.handleRestartUpload.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowSizeChange);
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowSizeChange);
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  handleWindowSizeChange() {
    const newWidth = window.innerWidth;
    const { isSmallDevice } = this.state;
    if (isSmallDevice && newWidth > 768) {
      this.setState({ isSmallDevice: false });
    } else if (!isSmallDevice && newWidth <= 768) {
      this.setState({ isSmallDevice: true });
    }
  }

  handleKeyDown(event) {
    if (this.slider) {
      if (event.keyCode === 37) {
        this.slider.slickPrev();
      } else if (event.keyCode === 39) {
        this.slider.slickNext();
      }
    }
  }

  handleShowDetailsClick() {
    this.setState(prevState => ({ showDetails: !prevState.showDetails }));
  }

  handleRestartUpload() {
    RestartProductionMutation.commit(
      environment,
      this.props.eventSgId,
      (error, status) => {
        if (!error) {
          this.setState({ uploadRestarted: true });
        }
      }
    );
  }

  handleDownloadCertificate(id, startNumber) {
    this._addToPendingArray(id, startNumber);
    // 1. Generate certificate
    GenerateEventCertificateMutation.commit(
      environment,
      this.props.eventSgId,
      id,
      startNumber,
      (error, url) => {
        if (!error) {
          // 2. Trigger download of certificate
          this.hiddenLinkRef.current.href = url;
          this.hiddenLinkRef.current.click();
        }
        this._removeFromPendingArray(id, startNumber);
      }
    );
  }

  getSocialMediaUrl = edge => {
    const socialMedia = getPhotoVariant(edge.node.variants, SOCIAL_MEDIA);
    return socialMedia ? socialMedia.url : null;
  };

  getOriginalUrl = edge => {
    const original = getPhotoVariant(edge.node.variants, ORIGINAL);
    return original ? original.url : null;
  };

  _addToPendingArray = (id, startNumber) => {
    this.setState(prevState => {
      let newArr = [...prevState.pendingRequests];
      newArr.push({ id, startNumber });
      return { pendingRequests: newArr };
    });
  };

  _removeFromPendingArray = (id, startNumber) => {
    this.setState(prevState => {
      let copy = [...prevState.pendingRequests];
      remove(copy, o => o.startNumber === startNumber && o.id === id);
      return { pendingRequests: copy };
    });
  };

  _getBestVariantAvailable(variants) {
    let photoVariant = null;
    if (this.props.isAccountEventPage) {
      photoVariant = getPhotoVariant(variants, ORIGINAL);
      if (!photoVariant) {
        photoVariant = getPhotoVariant(variants, SOCIAL_MEDIA);
      }
    }
    // The preview resolution will only be displayed in the photo search
    if (!this.props.isAccountEventPage) {
      if (!photoVariant) {
        photoVariant = getPhotoVariant(variants, PREVIEW);
      }
    }
    if (!photoVariant) {
      photoVariant = getPhotoVariant(variants, THUMBNAIL);
    }
    return photoVariant;
  }

  render() {
    if (!this.props.open) {
      return null;
    }

    const {
      isSmallDevice,
      uploadRestarted,
      showDetails,
      pendingRequests,
    } = this.state;

    const {
      eventHasSearchByFace,
      eventHasSecondFactor,
      media,
      slideIndex,
      fotoFlatProduct,
      singlePhotoProduct,
      startnumber,
      email,
      rechecks,
      isAccountEventPage,
      open,
      showRecheckView,
      onRequestClose,
      onSlide,
      onBuyFotoFlat,
      onBuySinglePhoto,
      onRequestRecheck,
      onShowRecheckView,
      onHideRecheckView,
      canRestartUpload,
      startNumbers,
    } = this.props;

    const currentMedia = media.edges[slideIndex];

    const sliderSettings = {
      infinite: true,
      lazyLoad: true,
      initialSlide: slideIndex,
      swipe: false,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            swipe: true,
          },
        },
      ],
    };

    const thumbnailPreviewDetails = (
      <div className="thumbnail-preview-details-container">
        {!isAccountEventPage && (
          <ThumbnailPreviewEventDetails
            photoSgId={currentMedia.node.sgId}
            shotDate={currentMedia.node.shotDate}
            fotoFlatProduct={fotoFlatProduct}
            singlePhotoProduct={singlePhotoProduct}
            showRecheckNotification={includes(rechecks, currentMedia.node.sgId)}
            onBuySinglePhoto={onBuySinglePhoto}
            onBuyFotoFlat={onBuyFotoFlat}
            onShowRecheckView={onShowRecheckView}
            eventHasSearchByFace={eventHasSearchByFace}
            showTagButton={startnumber !== 'no-photos-found' && startnumber !== undefined && startnumber !== '' ? true : false}
          />
        )}
        {isAccountEventPage && (
          <Fragment>
            <ThumbnailPreviewAccountDetails
              photoNodeId={currentMedia.node.id}
              photoSgId={currentMedia.node.sgId}
              shotDate={currentMedia.node.shotDate}
              socialMediaUrl={this.getSocialMediaUrl(currentMedia)}
              originalUrl={this.getOriginalUrl(currentMedia)}
              startNumbers={startNumbers}
              pendingRequests={pendingRequests}
              canRestartUpload={canRestartUpload}
              uploadRestarted={uploadRestarted}
              onRequestHighResolutionPhoto={this.handleRestartUpload}
              onDownloadCertificate={this.handleDownloadCertificate}
            />
            <a
              className="thumbnail-preview-hidden-link"
              download
              href=""
              ref={this.hiddenLinkRef}
            />
          </Fragment>
        )}
      </div>
    );

    return (
      <Dialog
        classes={{
          container: 'thumbnail-preview-dialog-container',
          paper: 'thumbnail-preview-dialog-paper',
        }}
        fullScreen={true}
        open={open}
        onClose={onRequestClose}
      >
        <RemoveScroll className="thumbnail-preview-remove-scroll">
          {!showRecheckView && (
            <IconButton
              classes={{ root: 'thumbnail-preview-dialog-close-icon-btn' }}
              onClick={onRequestClose}
            >
              <CloseIcon />
            </IconButton>
          )}

          {showRecheckView && (
            <div className="thumbnail-preview-recheck-view-container">
              <RequestRecheckView
                isStartNumberInputDisabled={eventHasSecondFactor || eventHasSearchByFace}
                startnumber={startnumber}
                email={email}
                onSubmit={(sn, email) =>
                  onRequestRecheck(currentMedia.node.sgId, sn, email)
                }
                onCancel={onHideRecheckView}
              />
            </div>
          )}
          {!showRecheckView && (
            <Fragment>
              <div className="thumbnail-preview-slider-container">
                <Slider
                  ref={c => (this.slider = c)}
                  className="react-slick-inner"
                  afterChange={onSlide}
                  {...sliderSettings}
                >
                  {media.edges.map(edge => {
                    const variant = this._getBestVariantAvailable(edge.node.variants);
                    return (
                      <div
                        key={edge.node.id}
                        className="thumbnail-preview-slider-img-container"
                      >
                        <Image
                          src={variant.url}
                          hasBackground={false}
                          hasHoverEffect={false}
                          hasProtectionOverlay={!isAccountEventPage}
                          hasFixHeight={true}
                        />
                      </div>
                    );
                  })}
                </Slider>
              </div>
              <div
                className="thumbnail-preview-details-controller"
                onClick={isSmallDevice ? this.handleShowDetailsClick : null}
              >
                {isSmallDevice && (
                  <IconButton className="thumbnail-preview-expand-btn">
                    {showDetails ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                  </IconButton>
                )}
                {isSmallDevice ? (
                  <Collapse in={showDetails} timeout="auto">
                    {thumbnailPreviewDetails}
                  </Collapse>
                ) : (
                  thumbnailPreviewDetails
                )}
              </div>
            </Fragment>
          )}
        </RemoveScroll>
      </Dialog>
    );
  }
}

ThumbnailPreview.defaultProps = {
  isAccountEventPage: false,
};

ThumbnailPreview.propTypes = {
  eventHasSecondFactor: PropTypes.bool,
  open: PropTypes.bool,
  isAccountEventPage: PropTypes.bool,
  showRecheckView: PropTypes.bool,
  canRestartUpload: PropTypes.bool,
  slideIndex: PropTypes.number,
  eventSgId: PropTypes.number,
  startnumber: PropTypes.string,
  email: PropTypes.string,
  rechecks: PropTypes.array,
  media: PropTypes.object,
  fotoFlatProduct: PropTypes.object,
  singlePhotoProduct: PropTypes.object,
  startNumbers: PropTypes.array,
  onSlide: PropTypes.func,
  onBuySinglePhoto: PropTypes.func,
  onBuyFotoFlat: PropTypes.func,
  onRequestRecheck: PropTypes.func,
  onRequestClose: PropTypes.func,
  onShowRecheckView: PropTypes.func,
  onHideRecheckView: PropTypes.func,
};

ThumbnailPreview.defaultProps = {
  isAccountEventPage: false,
};

export default ThumbnailPreview;
